import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import type { AccountInfo, Subscription, Plan, Badges, PaymentsConfig, PlanQuote } from "../../types";
import { fetchAccount, adminGrantSubscription, fetchPaymentsConfig, createHandoff, fetchQuotes, startLinkPayment, createBepaidOrder } from "../../utils/api";
import TierCard, { TIER_ORDER } from "./TierCard";
import WebApp from "@twa-dev/sdk";
import CheckoutSheet from "./CheckoutSheet";
import LinkPaySheet from "./LinkPaySheet";
import PayMethodSheet from "./PayMethodSheet";
import LinkedAccounts from "./LinkedAccounts";
import ReportSheet from "../common/ReportSheet";
import { AppHeader, Banner, BeeMark, Icon, Field } from "../common/ui";
import { useAppConfig } from "../../config/AppConfigContext";

interface Props {
  token: string;
  onExit: () => void;
  onLogout: () => void;
  badges?: Badges | null;
  /** Chat with Anya now lives here rather than on the main menu. */
  onChat?: () => void;
  /** Admin-only queues, likewise moved off the menu. */
  onTaskReview?: () => void;
  onAdminChats?: () => void;
  onAdminSettings?: () => void;
  /** "subscription" renders only subscription + plans (the ⚡ header button),
   *  instead of the whole profile. */
  focus?: "subscription";
  /** Open the checkout for this plan as soon as payment is available — set
   *  when the browser was opened from Telegram's "pay in the browser". */
  autoBuyPlanId?: string | null;
  onAutoBuyDone?: () => void;
  theme: "dark" | "light";
  onThemeChange: (t: "dark" | "light") => void;
}

function formatDate(iso: string, isRu: boolean): string {
  return new Date(iso).toLocaleDateString(isRu ? "ru-RU" : "en-US", {
    day: "numeric", month: "long", year: "numeric",
  });
}

function daysLabel(days: number, isRu: boolean): string {
  if (!isRu) return `${days} day${days !== 1 ? "s" : ""}`;
  const mod10 = days % 10;
  const mod100 = days % 100;
  if (mod10 === 1 && mod100 !== 11) return `${days} день`;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return `${days} дня`;
  return `${days} дней`;
}

function SubscriptionCard({ sub, isRu, canPay = false }: { sub: Subscription | null; isRu: boolean; canPay?: boolean }) {
  const { t } = useTranslation();

  if (!sub || !sub.is_active) {
    return (
      <div className="glass glass-lg" style={{ padding: 20 }}>
        <div className="row" style={{ gap: 10, marginBottom: 14 }}>
          <span style={{
            display: "grid", placeItems: "center", width: 40, height: 40, borderRadius: 12,
            background: "var(--glass-1)", color: "var(--text-3)", flex: "none",
          }}>
            <Icon name="lock" size={20} />
          </span>
          <div>
            <p className="eyebrow" style={{ marginBottom: 2 }}>{t("account.subscription")}</p>
            <p className="t-h" style={{ margin: 0, color: "var(--text-2)" }}>{t("account.noSubscription")}</p>
          </div>
        </div>
        <p className="t-sm muted" style={{ lineHeight: 1.5, marginBottom: 14 }}>
          {t("account.unlockAllDesc")}
        </p>
        <div className="banner banner-info">
          <Icon name="info" size={18} />
          <span>{canPay ? t("pay.choosePlan") : t("account.renewContact")}</span>
        </div>
      </div>
    );
  }

  const expiresAt = new Date(sub.expires_at);
  const startedAt = new Date(sub.started_at);
  const totalMs = expiresAt.getTime() - startedAt.getTime();
  const usedMs = Date.now() - startedAt.getTime();
  const pct = Math.max(0, Math.min(100, Math.round((1 - usedMs / totalMs) * 100)));
  const expiring = sub.days_left <= 7;

  return (
    <div className="glass glass-lg" style={{ padding: 20 }}>
      <div className="between" style={{ marginBottom: 14 }}>
        <div className="row" style={{ gap: 10 }}>
          <span style={{
            display: "grid", placeItems: "center", width: 40, height: 40, borderRadius: 12,
            background: "linear-gradient(155deg, var(--accent-fill), var(--accent-fill-2))",
            color: "var(--accent-ink)", flex: "none",
            boxShadow: "inset 0 1px 0 rgba(255,255,255,0.5)",
          }}>
            <Icon name="sparkles" size={20} />
          </span>
          <div>
            <p className="eyebrow" style={{ marginBottom: 2 }}>{t("account.subscription")}</p>
            <p className="t-h" style={{ margin: 0 }}>{t("account.active")}</p>
          </div>
        </div>
        <span className="badge badge-success">
          <Icon name="check" size={11} stroke={2.6} /> {t("account.active")}
        </span>
      </div>

      {/* Days remaining — hero number */}
      <div style={{
        padding: "14px 16px",
        borderRadius: "var(--r-md)",
        background: expiring ? "color-mix(in oklab, var(--warning) 12%, transparent)" : "var(--accent-tint)",
        marginBottom: 14,
        display: "flex",
        alignItems: "center",
        gap: 14,
      }}>
        <span className="t-display" style={{ color: expiring ? "var(--warning)" : "var(--accent-text)", lineHeight: 1 }}>
          {sub.days_left}
        </span>
        <div>
          <p className="t-sm" style={{ margin: 0, fontWeight: 700 }}>
            {daysLabel(sub.days_left, isRu)}
          </p>
          <p className="t-xs muted-2" style={{ margin: 0 }}>
            {t("account.activeUntil")} {formatDate(sub.expires_at, isRu)}
          </p>
        </div>
      </div>

      {/* Progress bar */}
      <div className="progress" style={{ marginBottom: 8 }}>
        <div className="progress__fill" style={{ width: `${pct}%` }} />
      </div>
      <p className="t-xs muted-2" style={{ marginBottom: 14, textAlign: "right" }}>
        {pct}% {t("account.remaining")}
      </p>

      {sub.granted_by_id && (
        <p className="t-xs muted-2" style={{ textAlign: "center" }}>
          <Icon name="heart" size={12} /> {t("account.grantedByAdmin")}
        </p>
      )}

      {expiring && (
        <div style={{ marginTop: 12 }}>
          <Banner kind="error">
            {t("account.expiresWarning")} {daysLabel(sub.days_left, isRu)}. {canPay ? t("pay.renewOnline") : `${t("account.renewContact")}.`}
          </Banner>
        </div>
      )}
    </div>
  );
}

/** A plan can be bought online when it has a dollar price and says what it unlocks. */
function sellableOnline(plan: Plan): boolean {
  return Number(plan.price_usd) > 0 && (plan.grants?.length ?? 0) > 0;
}

/** 30 → "1 месяц", 365 → "1 год", 14 → "14 дней" — how people say durations. */
function formatDuration(days: number, isRu: boolean): string {
  const plural = (n: number, one: string, few: string, many: string) => {
    const m10 = n % 10, m100 = n % 100;
    if (m10 === 1 && m100 !== 11) return one;
    if (m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20)) return few;
    return many;
  };
  if (days >= 360 && days % 365 <= 5) {
    const y = Math.round(days / 365);
    return isRu ? `${y} ${plural(y, "год", "года", "лет")}` : `${y} year${y > 1 ? "s" : ""}`;
  }
  if (days >= 28 && days % 30 <= 2) {
    const m = Math.round(days / 30);
    return isRu ? `${m} ${plural(m, "месяц", "месяца", "месяцев")}` : `${m} month${m > 1 ? "s" : ""}`;
  }
  return isRu ? `${days} ${plural(days, "день", "дня", "дней")}` : `${days} day${days !== 1 ? "s" : ""}`;
}

/** Group a whole price into thousands: 4990 → "4 990". */
function groupPrice(price: number | string, isRu: boolean): string {
  const n = Number(price);
  if (!Number.isFinite(n)) return String(price);
  return n.toLocaleString(isRu ? "ru-RU" : "en-US", { maximumFractionDigits: 2 });
}

/**
 * One plan, written so it can be compared at a glance: how long it lasts, what
 * it opens — and what it does NOT open that a neighbouring plan does (that is
 * the whole difference between «Курс» and «Курс с обратной связью») — then the
 * price and one clear action.
 */
function PlanCard({
  plan, allPlans, currency, contact, isRu, onBuy, onBrowserBuy, featured = false,
}: {
  plan: Plan; allPlans: Plan[]; currency: string; contact: string; isRu: boolean; onBuy?: () => void;
  /** Inside Telegram: leave for the browser, signed in, to pay there. */
  onBrowserBuy?: () => void;
  featured?: boolean;
}) {
  const { t } = useTranslation();
  const { config } = useAppConfig();
  const [tapped, setTapped] = useState(false);

  const title = (isRu ? plan.title_ru : plan.title_en) || plan.title_ru;
  const badge = (isRu ? plan.badge_ru : plan.badge_en) || undefined;
  const shownPrice = isRu ? plan.price : plan.price_usd;
  const priced = Number(shownPrice) > 0;
  const grants = plan.grants ?? [];
  const blockName = (key: string) => {
    const b = (config.blocks ?? []).find((x) => x.key === key);
    return b ? (isRu ? b.title_ru : b.title_en) : key;
  };

  // Main duration = the longest block in it (a course is "a year", even if the
  // tutor part lasts 14 days); a plan without contents falls back to `days`.
  const mainDays = grants.length ? Math.max(...grants.map((g) => g.days)) : plan.days;
  const included = new Set(grants.map((g) => g.block));
  // Show as "not included" only what a COMPARABLE plan (one sharing a block
  // with this one) includes — otherwise every card would list every block.
  const missing = Array.from(new Set(
    allPlans
      .filter((o) => o.id !== plan.id && (o.grants ?? []).some((g) => included.has(g.block)))
      .flatMap((o) => (o.grants ?? []).map((g) => g.block)),
  )).filter((b) => !included.has(b));

  const act = () => {
    if (onBuy) { onBuy(); return; }
    if (contact) { window.open(`https://t.me/${contact}`, "_blank", "noopener,noreferrer"); return; }
    setTapped(true);
  };

  return (
    <article className={"plan-card" + (featured ? " is-featured" : "")}>
      {badge && <span className="plan-card__badge">{badge}</span>}
      <h3 className="plan-card__title">{title}</h3>
      <p className="plan-card__term">
        <Icon name="clock" size={14} /> {t("planCard.access", { term: formatDuration(mainDays, isRu) })}
      </p>

      {(grants.length > 0 || missing.length > 0) && (
        <ul className="plan-card__list">
          {grants.map((g) => (
            <li key={g.block}>
              <span className="plan-card__yes"><Icon name="check" size={13} stroke={2.8} /></span>
              <span>{blockName(g.block)}</span>
              {g.days !== mainDays && <span className="plan-card__days">{formatDuration(g.days, isRu)}</span>}
            </li>
          ))}
          {missing.map((b) => (
            <li key={b} className="is-missing">
              <span className="plan-card__no"><Icon name="x" size={13} stroke={2.6} /></span>
              <span>{blockName(b)}</span>
            </li>
          ))}
        </ul>
      )}

      <div className="plan-card__foot">
        {priced ? (
          <>
            <p className="plan-card__price">
              <span>{groupPrice(shownPrice, isRu)}</span> <small>{currency}</small>
              <em>{t("planCard.oneTime")}</em>
            </p>
            {onBuy ? (
              <>
                <button className="btn btn-primary btn-block plan-card__cta" onClick={act}>
                  {t("planCard.payPaypal", { price: groupPrice(plan.price_usd, false) })}
                </button>
                <p className="plan-card__methods"><Icon name="lock" size={12} /> {t("planCard.cardNoAccount")}</p>
              </>
            ) : onBrowserBuy ? (
              <>
                <button className="btn btn-primary btn-block plan-card__cta" onClick={onBrowserBuy}>
                  <Icon name="globe" size={16} /> {t("planCard.payInBrowser", { price: groupPrice(plan.price_usd, false) })}
                </button>
                <p className="plan-card__methods">{t("planCard.browserHint")}</p>
              </>
            ) : contact ? (
              <button className="btn btn-glass btn-block plan-card__cta" onClick={act}>
                <Icon name="send" size={16} /> {t("planCard.writeAdmin")}
              </button>
            ) : tapped ? (
              <p className="t-xs" style={{ color: "var(--accent-text)", margin: 0 }}>{t("account.paymentSoon")}</p>
            ) : (
              <button className="btn btn-glass btn-block plan-card__cta" onClick={act}>{t("planCard.choose")}</button>
            )}
          </>
        ) : (
          <span className="badge" style={{ justifySelf: "start" }}>{t("account.comingSoon")}</span>
        )}
      </div>
    </article>
  );
}

function AdminGrantPanel({ token }: { token: string }) {
  const { t, i18n } = useTranslation();
  const { config } = useAppConfig();
  const isRu = !i18n.language?.startsWith("en");
  const [target, setTarget] = useState("");
  const [days, setDays] = useState("30");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: "success" | "error"; text: string } | null>(null);
  // Everything ticked by default: granting the whole course is the common case,
  // and it keeps this form doing what it did before blocks existed.
  const [picked, setPicked] = useState<Set<string> | null>(null);
  const blocks = config.blocks ?? [];
  const chosen = picked ?? new Set(blocks.map((b) => b.key));
  const toggle = (key: string) => {
    const next = new Set(chosen);
    if (next.has(key)) next.delete(key); else next.add(key);
    setPicked(next);
  };

  const grant = async () => {
    const raw = target.trim();
    const d = parseInt(days, 10);
    if (!raw || !d || d <= 0) {
      setMsg({ kind: "error", text: t("admin.invalid") });
      return;
    }
    if (chosen.size === 0) {
      setMsg({ kind: "error", text: t("admin.pickBlock") });
      return;
    }
    const at = raw.indexOf("@");
    const isEmail = at > 0 && raw.slice(at + 1).includes(".");
    const ref = raw.replace(/^@/, "");
    setBusy(true);
    setMsg(null);
    try {
      const blockList = [...chosen];
      const body = isEmail
        ? { email: raw, days: d, blocks: blockList }
        : ref.match(/^\d+$/)
          ? { telegram_id: Number(ref), days: d, blocks: blockList }
          : { username: ref, days: d, blocks: blockList };
      const access = await adminGrantSubscription(body, token);
      const owned = access.blocks.filter((b) => b.owned);
      if (access.pending_until) {
        // No account yet — the promise waits for the first login.
        const names = owned.map((b) => (isRu ? b.title_ru : b.title_en)).join(", ");
        const until = new Date(access.pending_until).toLocaleDateString();
        setMsg({ kind: "success", text: t("admin.pendingSaved", { name: access.name, blocks: names, until }) });
      } else {
        // Report what the student actually ends up with, per block — a grant
        // touches several subscriptions and no single expiry describes it.
        const summary = owned
          .map((b) => `${isRu ? b.title_ru : b.title_en} — ${b.days_left} ${t("account.daysAbbr")}`)
          .join(", ");
        setMsg({ kind: "success", text: `${t("admin.granted")}: ${access.name} · ${summary}` });
      }
      setTarget("");
    } catch (e) {
      const m = e instanceof Error ? e.message : "";
      setMsg({ kind: "error", text: m.includes("404") ? t("admin.notFound") : t("common.error") });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="glass glass-lg" style={{ padding: 20 }}>
      <div className="row" style={{ gap: 10, marginBottom: 6 }}>
        <span style={{
          display: "grid", placeItems: "center", width: 40, height: 40, borderRadius: 12,
          background: "var(--violet-tint)", color: "var(--violet-text)", flex: "none",
        }}>
          <Icon name="sparkles" size={20} />
        </span>
        <div>
          <p className="eyebrow" style={{ marginBottom: 2 }}>{t("admin.title")}</p>
          <p className="t-h" style={{ margin: 0 }}>{t("admin.grantTitle")}</p>
        </div>
      </div>
      <p className="t-sm muted" style={{ lineHeight: 1.5, marginBottom: 14 }}>{t("admin.grantDesc")}</p>

      <div className="stack" style={{ "--gap": "10px" } as React.CSSProperties}>
        <Field icon="user" value={target} onChange={setTarget} placeholder={t("admin.targetPlaceholder")} />
        <Field icon="clock" type="number" value={days} onChange={setDays} placeholder={t("admin.daysPlaceholder")} />

        {/* What to grant. All ticked by default — untick to sell a single block. */}
        <div>
          <p className="t-xs muted-2" style={{ margin: "2px 0 7px" }}>{t("admin.whatToGrant")}</p>
          <div className="stack" style={{ "--gap": "7px" } as React.CSSProperties}>
            {blocks.map((b) => {
              const on = chosen.has(b.key);
              return (
                <button
                  key={b.key}
                  onClick={() => toggle(b.key)}
                  role="switch"
                  aria-checked={on}
                  className="glass glass-1"
                  style={{
                    width: "100%", display: "flex", alignItems: "center", gap: 10,
                    padding: "9px 11px", borderRadius: 10, cursor: "pointer",
                    textAlign: "left", color: "inherit",
                    border: on ? "1px solid var(--violet-text)" : "1px solid var(--border)",
                  }}
                >
                  <span style={{
                    width: 20, height: 20, borderRadius: 6, flex: "none",
                    display: "grid", placeItems: "center",
                    background: on ? "var(--violet-fill)" : "var(--glass-3)",
                    color: on ? "var(--violet-ink)" : "transparent",
                  }}>
                    <Icon name="check" size={13} stroke={3} />
                  </span>
                  <span className="t-sm" style={{ fontWeight: 700 }}>
                    {isRu ? b.title_ru : b.title_en}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
        <button className="btn btn-violet btn-block" onClick={grant} disabled={busy}>
          {busy ? <span className="spinner" /> : <><Icon name="check" size={17} /> {t("admin.grantBtn")}</>}
        </button>
      </div>
      {msg && <div style={{ marginTop: 12 }}><Banner kind={msg.kind}>{msg.text}</Banner></div>}
    </div>
  );
}

/** One tappable row in the profile: icon, label, optional count. */
function NavRow({
  icon, accent, title, desc, count, onClick,
}: {
  icon: string; accent: "amber" | "violet"; title: string; desc: string;
  count?: number; onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="glass"
      style={{
        width: "100%", border: "none", color: "inherit", cursor: "pointer",
        padding: "13px 15px", display: "flex", alignItems: "center", gap: 12,
        borderRadius: "var(--r-md)", textAlign: "left",
      }}
    >
      <span style={{
        display: "grid", placeItems: "center", width: 38, height: 38, borderRadius: 11, flex: "none",
        background: accent === "violet" ? "var(--violet-tint)" : "var(--accent-tint)",
        color: accent === "violet" ? "var(--violet-text)" : "var(--accent-text)",
      }}>
        <Icon name={icon} size={19} />
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span className="t-h" style={{ display: "block" }}>{title}</span>
        <span className="t-xs muted-2" style={{ display: "block" }}>{desc}</span>
      </span>
      {!!count && count > 0 && (
        <span className="badge badge-violet" style={{ fontWeight: 800 }}>{count}</span>
      )}
      <Icon name="chevronRight" size={18} />
    </button>
  );
}

export default function AccountView({
  token, onExit, onLogout, badges, onChat, onTaskReview, onAdminChats, onAdminSettings, focus, autoBuyPlanId, onAutoBuyDone, theme, onThemeChange,
}: Props) {
  const { t, i18n } = useTranslation();
  const isRu = !i18n.language?.startsWith("en");

  const [account, setAccount] = useState<AccountInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchAccount(token)
      .then(setAccount)
      .catch((e) => setError(e instanceof Error ? e.message : t("common.error")))
      .finally(() => setLoading(false));
  }, [token, t]);


  const displayName = account
    ? [account.first_name, account.last_name].filter(Boolean).join(" ") || account.username || account.email || "—"
    : "—";

  // Plans and their prices are admin-controlled — see the Настройки screen.
  const { config } = useAppConfig();
  const plans = config.plans;
  // Online payment: the server decides, per session, whether to offer it — a
  // Telegram session is always told "no", so nothing here sniffs the platform.
  const [payments, setPayments] = useState<PaymentsConfig | null>(null);
  const [buying, setBuying] = useState<Plan | null>(null);
  // What each plan costs this user now (renewal / upgrade with credit) — the
  // server's numbers; refreshed after a purchase.
  const [quotes, setQuotes] = useState<Record<string, PlanQuote>>({});
  const loadQuotes = useCallback(() => {
    fetchQuotes(token).then((qs) => setQuotes(Object.fromEntries(qs.map((q) => [q.plan_id, q])))).catch(() => setQuotes({}));
  }, [token]);
  useEffect(() => { if (focus === "subscription") loadQuotes(); }, [focus, loadQuotes]);
  // Tariffs: plans sharing a tier, shortest period first. Plans without a
  // tier (custom or retired-style) keep their own single card.
  const tiers = TIER_ORDER
    .map((tier) => plans.filter((p) => p.tier === tier).sort((a, b) => a.days - b.days))
    .filter((ps) => ps.length > 0);
  const untiered = plans.filter((p) => !p.tier);

  // Payment through a ready-made link (bePaid). The page opens AT ONCE on the
  // tap (Telegram only opens links straight from a tap); who opened it is
  // recorded in parallel, and the sheet then asks «Я оплатил(а)».
  const [linkPay, setLinkPay] = useState<{ plan: Plan; paymentId: string | null; failed: boolean } | null>(null);
  // «Как удобнее оплатить?» — the chosen plan waiting for a payment method.
  const [choosing, setChoosing] = useState<{ plan: Plan; quote?: PlanQuote } | null>(null);
  // A buyer from Belarus/Russia asking for a payment link (plan has none yet).
  const [linkRequest, setLinkRequest] = useState<Plan | null>(null);
  const inTelegram = !!WebApp.initData;
  const linkAllowed = !inTelegram || !!payments?.browser_checkout;
  // Automatic bePaid: the checkout page opens in this same tab (works inside
  // Telegram's webview too) and sends the buyer back to /?bepaid=<id>, where
  // App checks the payment. A failure is shown by PayMethodSheet.
  const bepaidAuto = !!payments?.bepaid_enabled;
  const payBepaid = async (plan: Plan) => {
    const { redirect_url } = await createBepaidOrder(plan.id, token, i18n.language || "ru");
    window.location.href = redirect_url;
  };
  const payByLink = (plan: Plan) => {
    if (!plan.pay_link) return;
    try {
      if (inTelegram) WebApp.openLink(plan.pay_link); else window.open(plan.pay_link, "_blank", "noopener");
    } catch { window.open(plan.pay_link, "_blank", "noopener"); }
    setLinkPay({ plan, paymentId: null, failed: false });
    startLinkPayment(plan.id, token)
      .then((r) => setLinkPay((cur) => (cur ? { ...cur, paymentId: r.payment_id } : cur)))
      .catch(() => setLinkPay((cur) => (cur ? { ...cur, failed: true } : cur)));
  };
  useEffect(() => {
    if (focus !== "subscription") return;
    fetchPaymentsConfig(token).then(setPayments).catch(() => setPayments(null));
  }, [token, focus]);
  const canPay = !!payments?.enabled && plans.some(sellableOnline);

  // Arrived from Telegram to pay for a particular plan: open its checkout once.
  useEffect(() => {
    if (!autoBuyPlanId || !payments?.enabled) return;
    const plan = plans.find((p) => p.id === autoBuyPlanId);
    if (plan && sellableOnline(plan)) setBuying(plan);
    onAutoBuyDone?.();
  }, [autoBuyPlanId, payments, plans, onAutoBuyDone]);

  // Inside Telegram: "pay in the browser". Telegram opens external links only
  // straight from a tap, so the one-time sign-in token is fetched AHEAD of the
  // tap (and replaced after use and before it expires), never awaited in it.
  const browserCheckout = !!payments?.browser_checkout && plans.some(sellableOnline);
  const [handoffToken, setHandoffToken] = useState<string | null>(null);
  const refreshHandoff = useCallback(() => {
    createHandoff(token).then((h) => setHandoffToken(h.token)).catch(() => setHandoffToken(null));
  }, [token]);
  useEffect(() => {
    if (!browserCheckout) return;
    refreshHandoff();
    const id = setInterval(refreshHandoff, 8 * 60 * 1000);   // tokens live 10 min
    return () => clearInterval(id);
  }, [browserCheckout, refreshHandoff]);
  const payInBrowser = (plan: Plan) => {
    if (!handoffToken) { refreshHandoff(); return; }
    const url = `${window.location.origin}/?handoff=${encodeURIComponent(handoffToken)}&buy=${encodeURIComponent(plan.id)}`;
    try { WebApp.openLink(url); } catch { window.open(url, "_blank", "noopener"); }
    setHandoffToken(null);
    refreshHandoff();          // a token works once — have the next one ready
  };
  // One card stands out: the most complete priced plan (most blocks, then the
  // dearest) — the natural "everything included" choice. Not every plan that
  // happens to carry a badge.
  const fullestId = [...plans]
    .filter((p) => Number(isRu ? p.price : p.price_usd) > 0)
    .sort((a, b) => ((b.grants?.length ?? 0) - (a.grants?.length ?? 0)) || (Number(b.price_usd) - Number(a.price_usd)))[0]?.id;
  const reloadAccount = () => { fetchAccount(token).then(setAccount).catch(() => {}); };
  // The ⚡ header button opens this screen focused on the subscription alone.
  const subOnly = focus === "subscription";

  return (
    <div className="beeapp__scroll">
      <AppHeader
        theme={theme}
        onThemeChange={onThemeChange}
        onBack={onExit}
        title={subOnly ? t("account.subscription") : t("account.title")}
        showLang={false}
      />

      <div className="wrap content-cap" style={{ paddingTop: 6 }}>
        {/* Profile card */}
        {!subOnly && (
        <div className="glass glass-lg anim-rise" style={{ padding: 20, marginBottom: 16 }}>
          <div className="row" style={{ gap: 14 }}>
            <div style={{
              width: 54, height: 54, borderRadius: "50%", flex: "none",
              background: "linear-gradient(155deg, var(--accent-fill), var(--accent-fill-2))",
              display: "grid", placeItems: "center",
              boxShadow: "inset 0 1px 0 rgba(255,255,255,0.5), 0 6px 16px -6px var(--accent-glow)",
            }}>
              <BeeMark size={54} />
            </div>
            <div style={{ minWidth: 0 }}>
              {loading ? (
                <>
                  <div className="skel" style={{ width: 120, height: 18, marginBottom: 8 }} />
                  <div className="skel" style={{ width: 80, height: 14 }} />
                </>
              ) : (
                <>
                  <div className="t-title" style={{ lineHeight: 1.1 }}>{displayName}</div>
                  {account?.username && (
                    <div className="t-sm muted" style={{ marginTop: 2 }}>@{account.username}</div>
                  )}
                  {account?.created_at && (
                    <div className="t-xs muted-2" style={{ marginTop: 4 }}>
                      {t("account.memberSince")} {formatDate(account.created_at, isRu)}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
        )}

        {error && <div style={{ marginBottom: 16 }}><Banner kind="error">{error}</Banner></div>}

        {/* Inbox-shaped destinations that used to be main-menu cards */}
        {!subOnly && (
        <div className="anim-rise stack" style={{ "--gap": "9px", marginBottom: 16 } as React.CSSProperties}>
          {onChat && (config.sections?.chat !== false || account?.role === "admin") && (
            <NavRow
              icon="message" accent="amber"
              title={t("chat.title")} desc={t("chat.menuDesc")}
              count={badges?.unread_chat}
              onClick={onChat}
            />
          )}
          {account?.role === "admin" && onTaskReview && (
            <NavRow
              icon="checkCircle" accent="amber"
              title={t("review.title")} desc={t("review.menuDesc")}
              count={badges?.pending_work}
              onClick={onTaskReview}
            />
          )}
          {account?.role === "admin" && onAdminChats && (
            <NavRow
              icon="inbox" accent="violet"
              title={t("chat.adminTitle")} desc={t("chat.adminMenuDesc")}
              count={badges?.unanswered_chats}
              onClick={onAdminChats}
            />
          )}
          {account?.role === "admin" && onAdminSettings && (
            <NavRow
              icon="sliders" accent="amber"
              title={t("adminSettings.title")} desc={t("adminSettings.menuDesc")}
              onClick={onAdminSettings}
            />
          )}
        </div>
        )}

        {/* Subscription + plans live on the ⚡ screen only — the profile is
            identity, messages and admin tools. */}
        {subOnly && (
        <div className="anim-rise" style={{ marginBottom: 16, animationDelay: "0.05s" }}>
          {loading ? (
            <div className="glass glass-lg" style={{ padding: 20 }}>
              <div className="skel" style={{ width: "60%", height: 20, marginBottom: 12 }} />
              <div className="skel" style={{ height: 72, borderRadius: "var(--r-md)", marginBottom: 12 }} />
              <div className="skel" style={{ height: 6, borderRadius: 99 }} />
            </div>
          ) : (
            <SubscriptionCard sub={account?.subscription ?? null} isRu={isRu} canPay={canPay || browserCheckout} />
          )}
        </div>
        )}

        {/* Admin panel — grant premium */}
        {!subOnly && account?.role === "admin" && (
          <div className="anim-rise" style={{ marginBottom: 16, animationDelay: "0.07s" }}>
            <AdminGrantPanel token={token} />
          </div>
        )}

        {/* Plans — hidden entirely when the admin has retired every plan */}
        {subOnly && plans.length > 0 && (
          <div className="anim-rise" style={{ marginBottom: 24, animationDelay: "0.1s" }}>
            <p className="eyebrow" style={{ marginBottom: 12 }}>{t("account.plans")}</p>
            {config.pricing_notice?.enabled && (isRu ? config.pricing_notice.text_ru : (config.pricing_notice.text_en || config.pricing_notice.text_ru)) && (
              <div className="launch-notice" role="note">
                <span className="launch-notice__icon"><Icon name="sparkles" size={18} /></span>
                <span>
                  <strong>{t("planCard.launchTitle")}</strong>
                  <span className="launch-notice__text">
                    {isRu ? config.pricing_notice.text_ru : (config.pricing_notice.text_en || config.pricing_notice.text_ru)}
                  </span>
                </span>
              </div>
            )}
            <div className="plan-grid">
              {tiers.map((ps, i) => (
                <TierCard
                  key={ps[0].tier ?? i}
                  tier={ps[0].tier ?? ""}
                  plans={ps}
                  quotes={quotes}
                  allTierPlans={tiers}
                  featured={i === tiers.length - 1 && tiers.length > 1}
                  currency={isRu ? config.currency : config.currency_en}
                  isRu={isRu}
                  canPayHere={!!payments?.enabled}
                  onBrowserBuy={browserCheckout ? (p) => payInBrowser(p) : undefined}
                  onLinkPay={linkAllowed || bepaidAuto ? payByLink : undefined}
                  bynRate={isRu ? config.byn_per_100_rub : undefined}
                  onChoose={(p, q) => setChoosing({ plan: p, quote: q })}
                  contact={config.contact_handle}
                />
              ))}
              {untiered.map((p) => (
                <PlanCard
                  key={p.id}
                  plan={p}
                  allPlans={plans}
                  featured={p.id === fullestId}
                  currency={isRu ? config.currency : config.currency_en}
                  contact={config.contact_handle}
                  isRu={isRu}
                  onBuy={payments?.enabled && sellableOnline(p) ? () => setBuying(p) : undefined}
                  onBrowserBuy={browserCheckout && sellableOnline(p) ? () => payInBrowser(p) : undefined}
                />
              ))}
            </div>
            {canPay || browserCheckout ? null : config.contact_handle && (
              <p className="t-xs muted-2" style={{ textAlign: "center", marginTop: 10 }}>
                {t("account.contactToBuy")} @{config.contact_handle}
              </p>
            )}
          </div>
        )}

        {!subOnly && account && (
          <div className="anim-rise" style={{ animationDelay: "0.12s", marginBottom: 14 }}>
            <LinkedAccounts account={account} token={token} onLinked={reloadAccount} />
          </div>
        )}

        {/* Logout */}
        {!subOnly && (
        <div className="anim-rise" style={{ animationDelay: "0.15s" }}>
          <button
            className="btn btn-ghost btn-block"
            onClick={onLogout}
            style={{ color: "var(--error)", marginBottom: 32 }}
          >
            <Icon name="logout" size={18} /> {t("menu.logout")}
          </button>
        </div>
        )}
      </div>
      {choosing && (
        <PayMethodSheet
          plan={choosing.plan}
          quote={choosing.quote}
          contact={config.contact_handle}
          onClose={() => setChoosing(null)}
          onRequestLink={() => { const p = choosing.plan; setChoosing(null); setLinkRequest(p); }}
          accountName={displayName !== "—" ? displayName : undefined}
          bepaidAuto={bepaidAuto}
          bepaidSandbox={payments?.bepaid_environment === "sandbox"}
          onBepaid={bepaidAuto ? () => payBepaid(choosing.plan)
            : linkAllowed && choosing.plan.pay_link ? () => { const p = choosing.plan; setChoosing(null); payByLink(p); } : undefined}
          onPaypal={payments?.enabled ? () => { const p = choosing.plan; setChoosing(null); setBuying(p); }
            : browserCheckout ? () => { const p = choosing.plan; setChoosing(null); payInBrowser(p); } : undefined}
        />
      )}
      {linkRequest && (
        <ReportSheet
          token={token}
          screen={t("payMethod.requestScreen")}
          initialCategory="payment"
          initialText={t("payMethod.requestText", {
            plan: (isRu ? linkRequest.title_ru : linkRequest.title_en) || linkRequest.title_ru,
            term: linkRequest.days >= 360 ? t("planCard.durYear") : t("planCard.durMonths", { count: Math.round(linkRequest.days / 30) }),
          })}
          onClose={() => setLinkRequest(null)}
        />
      )}
      {linkPay && (
        <LinkPaySheet
          plan={linkPay.plan}
          paymentId={linkPay.paymentId}
          failed={linkPay.failed}
          token={token}
          onClose={() => setLinkPay(null)}
        />
      )}
      {buying && payments && (
        <CheckoutSheet
          plan={buying}
          payments={payments}
          token={token}
          quote={quotes[buying.id]}
          onClose={() => { setBuying(null); loadQuotes(); }}
          onPaid={reloadAccount}
        />
      )}
    </div>
  );
}
